import React from 'react';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import Select from 'react-select';

import { GET_TOTEMS } from 'data/queries';
import { User } from 'lib/User';

import { TotemButton } from 'ui/Button';

import { PopupTitle, PopupSubtitle } from 'styles/pages/settings.styles';
import { TotemLabel, TotemSelect, TotemSelectInput } from 'styles';

import { createSelectOptionsWithValue } from 'utils';

export interface Totem {
    _id: string;
    address?: string;
    zipcode?: number;
    city?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    name?: string;
    number?: number;
}

interface ExistingTotemSelectionProps {
    title: string;
    description?: string;
    defaultTotemId?: string;
    validateNewAddress: () => void;
    validateExistingTotem: (totem: Totem | undefined) => void;
    isDuplicatingOrder?: boolean;
    setDestinationTotemId?: (totemId: string) => void;
    setIsCreatingNewTotem?: (isCreating: boolean) => void;
    setDeliveryInfo?: (deliveryInfo: string) => void;
}

export const ExistingTotemSelection: React.FC<ExistingTotemSelectionProps> = ({
    title,
    description,
    defaultTotemId,
    validateNewAddress,
    validateExistingTotem,
    isDuplicatingOrder = false,
}) => {
    const apolloClient = useApolloClient();
    const queryResult = apolloClient.readQuery<{ totemsOfUser: Totem[] } | null>({
        query: GET_TOTEMS,
    });
    // Le cache contient tous les totems de l'organisation : ne proposer comme destination que
    // ceux que l'utilisateur peut ouvrir, comme la nav (sinon l'API refuse la duplication).
    const totemsOfUser = (queryResult?.totemsOfUser ?? []).filter((totem) => User.canAccessTotem(totem._id));
    const totemAddressesToDuplicate = totemsOfUser.map((totem: Totem) => {
        const label = `${totem.address || 'Sans adresse'} (${totem.name || 'TOTEM #' + totem.number})`;
        return {
            value: totem._id,
            label,
        };
    });
    const totemAddressesToCreateNewFloor = totemsOfUser
        .filter((totem: Totem) => totem.address)
        .map((totem: Totem) => {
            const label = `Nouvel étage au ${totem.address} (${totem.name || 'TOTEM #' + totem.number})`;
            return {
                value: totem._id,
                label,
            };
        });
    const totemAddresses = isDuplicatingOrder ? totemAddressesToDuplicate : totemAddressesToCreateNewFloor;
    const newAddressOption = { value: 'newAddress', label: 'Nouvelle adresse' };
    const defaultAddress =
        totemAddresses.find((address: { value: string }) => address.value === defaultTotemId) || newAddressOption;
    const totemAddressOptions = createSelectOptionsWithValue([newAddressOption, ...totemAddresses], 'totemAddress');
    const handleConfirmAddress = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const target = e.target as typeof e.target & { address: { value: string } };
        if (target.address.value === 'newAddress') {
            validateNewAddress();
        } else {
            validateExistingTotem(totemsOfUser.find((totem: Totem) => totem._id === target.address.value));
        }
    };

    return (
        <Container>
            <PopupTitle>{title}</PopupTitle>
            {description && <PopupSubtitle>{description}</PopupSubtitle>}
            <TotemSelectionForm onSubmit={handleConfirmAddress}>
                <TotemLabel>
                    Adresse
                    <Select
                        name="address"
                        placeholder="Sélectionnez une adresse"
                        defaultValue={defaultAddress}
                        options={totemAddressOptions}
                        classNamePrefix="select-totem"
                        // @ts-ignore
                        component={TotemSelect}
                        inputComponent={TotemSelectInput}
                    />
                </TotemLabel>
                <TotemButton type="submit" data-test="confirm-address">
                    Suivant
                </TotemButton>
            </TotemSelectionForm>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 20px;
`;

const TotemSelectionForm = styled.form`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    gap: 15px;
`;
